import React, { useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { Box } from '@mui/material'
import { generatePath, useNavigate } from 'react-router-dom'

import { FetchErrorMessage } from '@/components'
import { B2CRegistriesRequest } from '@/@types/b2c.types.ts'
import { TableComponent } from '@/components/table'
import { useGetB2CRegistriesList } from '@/services'
import { useIsMobile } from '@/hooks'
import { APP_ROUTES } from '@/constants.ts'

import {
  defaultSettings,
  getColumnsSettings,
  KEY_PAGE,
  PartialRegistriesRequest,
} from '../default-settings-registries.tsx'
import { useAppDispatch, useAppSelector } from '@/stores/hooks.ts'
import { userSelectors } from '@/stores/slices/user-slice.ts'
import { useB2CRegistryPaymentsTableState } from '@/stores/slices/b2c-slice'
import { DefaultFiltersState } from '@/stores'
import { b2cRegistryPaymentsActions } from '@/stores/slices/b2c-slice/b2c-registry-payments-slice.ts'

const columnHelper = createColumnHelper<B2CRegistriesRequest>()
const defaultColumns = getColumnsSettings(columnHelper)

export const B2CRequestsRegistriesTable = (): React.ReactElement => {
  const { isLoading, data, isError, refetch, isFetching } = useGetB2CRegistriesList()
  const navigate = useNavigate()
  const settings = useAppSelector((state) => userSelectors.personalSettings(state, KEY_PAGE))
  const { page, showCount } = useB2CRegistryPaymentsTableState()
  const isMobile = useIsMobile()
  const dispatch = useAppDispatch()

  const handleViewTransaction = (row: B2CRegistriesRequest) => () => {
    navigate(generatePath(APP_ROUTES.B2CRegistry, { id: row.id.toString() }))
  }

  const handlePaginationParam = (param: keyof DefaultFiltersState) => (value: number) =>
    dispatch(
      b2cRegistryPaymentsActions.setFilter({
        filterKey: param,
        filterValue: value,
      })
    )

  const b2cRequestCols = useMemo(() => {
    const format = isMobile ? 'mobile' : 'desktop'

    const desktopSettings = (settings?.[format] ?? Object.keys(defaultSettings)) as Array<
      keyof PartialRegistriesRequest
    >

    const dynamicSettings = desktopSettings.map((e) => defaultColumns[e])
    return [...dynamicSettings]
  }, [settings, isMobile])

  return (
    <FetchErrorMessage isError={isError} refetch={refetch} isFetching={isFetching}>
      <Box mt={3}>
        <TableComponent<B2CRegistriesRequest>
          rowKey="id"
          isLoading={isLoading || isFetching}
          columns={b2cRequestCols}
          page={page}
          size={showCount}
          totalPages={data?.data.totalPages}
          data={data?.data?.content ?? []}
          onPageChange={handlePaginationParam('page')}
          onSizeChange={handlePaginationParam('showCount')}
          onRowClick={handleViewTransaction}
        />
      </Box>
    </FetchErrorMessage>
  )
}
